import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  makeApi,
  composeAddress,
  type IssuePriority,
  type Me,
} from '../api/client';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 10 * 1024 * 1024;

interface Option {
  projectId: string;
  addressId: string;
  label: string;
}

export function NewIssuePage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [me, setMe] = useState<Me | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<IssuePriority>('medium');
  const [linkKey, setLinkKey] = useState<string>('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitLabel, setSubmitLabel] = useState('Submit issue');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.me().then(setMe).catch((e) => setError(String(e)));
  }, [api]);

  const options = useMemo<Option[]>(() => {
    if (!me) return [];
    const opts: Option[] = [];
    for (const a of me.addresses) {
      if (a.claimStatus !== 'primary') continue;
      for (const p of a.projects) {
        opts.push({
          projectId: p.projectId,
          addressId: a.addressId,
          label: `${composeAddress(a)} — ${p.projectReferenceNumber}`,
        });
      }
    }
    return opts;
  }, [me]);

  // When the customer owns exactly one project+property combo there is
  // nothing to choose — auto-select it so the form is one fewer click and the
  // dropdown can collapse to a read-only line.
  useEffect(() => {
    if (options.length === 1) {
      const only = options[0];
      const key = `${only.projectId}|${only.addressId}`;
      setLinkKey((prev) => (prev === key ? prev : key));
    }
  }, [options]);

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const valid: File[] = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        setError(`${f.name}: only image files (JPEG, PNG, WebP, GIF) are allowed`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        setError(`${f.name}: exceeds 10 MB limit`);
        continue;
      }
      valid.push(f);
    }
    setPendingFiles((prev) => [...prev, ...valid]);
  }

  // One stable object URL per File reference. Keyed on File identity (not
  // index) so typing in the description textarea doesn't churn the cache and
  // doesn't re-trigger createObjectURL (which would otherwise reload the img).
  const previewUrlCacheRef = useRef<Map<File, string>>(new Map());
  const previewUrls = pendingFiles.map((f) => {
    const cache = previewUrlCacheRef.current;
    let url = cache.get(f);
    if (!url) {
      url = URL.createObjectURL(f);
      cache.set(f, url);
    }
    return url;
  });
  useEffect(() => {
    const cache = previewUrlCacheRef.current;
    const live = new Set(pendingFiles);
    for (const [f, url] of cache) {
      if (!live.has(f)) {
        URL.revokeObjectURL(url);
        cache.delete(f);
      }
    }
  }, [pendingFiles]);
  useEffect(() => {
    const cache = previewUrlCacheRef.current;
    return () => {
      cache.forEach((u) => URL.revokeObjectURL(u));
      cache.clear();
    };
  }, []);

  function removeFile(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!linkKey) return;
    const [projectId, addressId] = linkKey.split('|');
    setSubmitting(true);
    try {
      setSubmitLabel('Creating issue…');
      const issue = await api.createIssue({
        projectId,
        addressId,
        title: title.trim(),
        description: description.trim(),
        priority,
      });

      for (let i = 0; i < pendingFiles.length; i++) {
        const file = pendingFiles[i];
        setSubmitLabel(`Uploading file ${i + 1} of ${pendingFiles.length}…`);
        try {
          const { uploadUrl } = await api.requestAttachmentUpload(issue.issueId, {
            filename: file.name,
            contentType: file.type,
            size: file.size,
          });
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          });
        } catch {
          setError(`Issue created but failed to upload ${file.name}. You can add it from the issue page.`);
        }
      }

      navigate(`/issues/${issue.issueId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create issue');
      setSubmitLabel('Submit issue');
    } finally {
      setSubmitting(false);
    }
  }

  if (!me) return <div className="text-gray-500">Loading…</div>;
  if (me.accountStatus !== 'approved') {
    return (
      <div className="alert-red">
        Your account is not approved to file issues yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-white">New issue</h1>
      <form onSubmit={onSubmit} className="space-y-4 card">
        <div>
          <label className="label">Which property is this issue about?</label>
          {options.length === 1 ? (
            <div
              className="input bg-gray-900/60 text-gray-200 cursor-not-allowed"
              aria-readonly="true"
              data-testid="property-locked"
            >
              {options[0].label}
            </div>
          ) : (
            // Radio list (not <select>) — the native iOS Safari select picker
            // crashes when the underlying React tree re-renders while it's
            // open, which happens easily here because typing in the
            // description textarea churns state on every keystroke. Same
            // pattern we use on SignupPage step 2.
            <div
              className="max-h-72 overflow-y-auto border border-gray-700 rounded-md divide-y divide-gray-800 bg-gray-900"
              role="radiogroup"
              aria-label="Which property is this issue about?"
            >
              {options.map((o) => {
                const key = `${o.projectId}|${o.addressId}`;
                const checked = linkKey === key;
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ${
                      checked
                        ? 'bg-red-900/20 text-white'
                        : 'text-gray-200 hover:bg-gray-800/60'
                    }`}
                  >
                    <input
                      type="radio"
                      name="new-issue-property"
                      value={key}
                      checked={checked}
                      onChange={(e) => setLinkKey(e.target.value)}
                      className="accent-red-600"
                      required
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <label className="label" htmlFor="new-issue-title">Title</label>
          <input
            id="new-issue-title"
            className="input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
          />
        </div>
        <div>
          <div className="text-xs text-amber-400 mb-1">
            Please do not include sensitive personal or confidential information unless necessary.
          </div>
          <label className="label">Description</label>
          <textarea
            className="input min-h-[140px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={10_000}
          />
          {pendingFiles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {pendingFiles.map((f, i) => (
                <div
                  key={i}
                  className="relative group border border-gray-700 rounded overflow-hidden bg-gray-900"
                >
                  <img
                    src={previewUrls[i]}
                    alt={f.name}
                    className="h-20 w-20 object-cover"
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${f.name}`}
                    className="absolute top-0 right-0 bg-black/70 text-white text-xs leading-none px-1.5 py-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    onClick={() => removeFile(i)}
                    disabled={submitting}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => fileRef.current?.click()}
              disabled={submitting}
            >
              Add image
            </button>
            <span className="ml-2 text-xs text-gray-500">
              Images only, up to 10 MB each.
            </span>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              multiple
              accept={ALLOWED_TYPES.join(',')}
              onChange={onFileChange}
            />
          </div>
        </div>
        <div>
          <label className="label">Priority</label>
          <select
            className="input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as IssuePriority)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        {error && <div className="text-sm text-red-400">{error}</div>}
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting || !title || !linkKey}
        >
          {submitting ? submitLabel : 'Submit issue'}
        </button>
      </form>
    </div>
  );
}
